/** Candidate-only product access and route policy. */

import {
  CURRENT_FAMILY_PARENT_ONBOARDING_VERSION,
  readParentOnboardingVersion,
} from '../family/familyAccountHome';

export type CurrentProductArea = 'sat' | 'family';

export type ProductAccessState =
  | { kind: 'missing' }
  | { kind: 'null' }
  | { kind: 'malformed' }
  | { kind: 'empty' }
  | { kind: 'unknown'; values: string[] }
  | { kind: 'learn' }
  | { kind: 'duplicate' }
  | { kind: 'multi-product' }
  | { kind: 'area'; area: CurrentProductArea };

export type ProductAccessUser = {
  family_account_type?: unknown;
  family_parent_onboarding_version?: unknown;
  product_access?: unknown;
  [key: string]: unknown;
};

export type CandidateAccessRecoveryReason =
  | 'access-missing'
  | 'access-null'
  | 'access-malformed'
  | 'access-unknown'
  | 'access-learn'
  | 'access-duplicate'
  | 'access-multi-product'
  | 'account-access-mismatch'
  | 'no-usable-area'
  | 'unsupported-route';

export type CandidateAccessResolution =
  | { kind: 'allow'; area: CurrentProductArea | 'learn' | 'account-setup' }
  | {
    kind: 'redirect';
    destination: '/practice' | '/family' | '/choose-role';
    reason: 'authorized-area' | 'role-setup' | 'parent-onboarding' | 'setup-complete';
  }
  | { kind: 'recover'; reason: CandidateAccessRecoveryReason };

const KNOWN_PRODUCTS = new Set(['sat', 'family', 'learn']);

export function classifyProductAccess(user: ProductAccessUser): ProductAccessState {
  if (!Object.prototype.hasOwnProperty.call(user, 'product_access')) return { kind: 'missing' };

  const value = user.product_access;
  if (value === null) return { kind: 'null' };
  if (!Array.isArray(value) || value.some((entry) => typeof entry !== 'string')) {
    return { kind: 'malformed' };
  }
  if (value.length === 0) return { kind: 'empty' };

  const unknown = [...new Set(value.filter((entry) => !KNOWN_PRODUCTS.has(entry)))];
  if (unknown.length > 0) return { kind: 'unknown', values: unknown };
  if (new Set(value).size !== value.length) return { kind: 'duplicate' };
  if (value.length > 1) return { kind: 'multi-product' };
  if (value[0] === 'learn') return { kind: 'learn' };
  return { kind: 'area', area: value[0] as CurrentProductArea };
}

function normalizedPath(pathname: string): string {
  const path = (pathname || '').split(/[?#]/, 1)[0].replace(/\/+$/, '').toLowerCase();
  return path || '/';
}

function routeArea(pathname: string): CurrentProductArea | 'learn' | 'account-setup' | null {
  const path = normalizedPath(pathname);
  if (path === '/learn' || path.startsWith('/learn/')) return 'learn';
  if (path === '/sat' || path.startsWith('/sat/')) return 'sat';
  if (path === '/family' || path.startsWith('/family/')) return 'family';
  if (path === '/account/choose-role') return 'account-setup';
  return null;
}

function homeFor(area: CurrentProductArea): '/practice' | '/family' {
  return area === 'sat' ? '/practice' : '/family';
}

function recoveryFor(state: Exclude<ProductAccessState, { kind: 'area' } | { kind: 'empty' }>): CandidateAccessResolution {
  return { kind: 'recover', reason: `access-${state.kind}` as CandidateAccessRecoveryReason };
}

export function resolveCandidateProductAccess({
  user,
  pathname,
  chooseRoleSuppressed = false,
}: {
  user: ProductAccessUser;
  pathname: string;
  chooseRoleSuppressed?: boolean;
}): CandidateAccessResolution {
  const requestedArea = routeArea(pathname);
  if (requestedArea === 'learn') return { kind: 'allow', area: 'learn' };
  if (requestedArea === null) return { kind: 'recover', reason: 'unsupported-route' };

  const access = classifyProductAccess(user);
  if (access.kind === 'empty') {
    if (user.family_account_type == null) {
      return requestedArea === 'account-setup'
        ? { kind: 'allow', area: 'account-setup' }
        : { kind: 'redirect', destination: '/choose-role', reason: 'role-setup' };
    }
    if (user.family_account_type === 'student' || user.family_account_type === 'guardian') {
      return { kind: 'recover', reason: 'no-usable-area' };
    }
    return { kind: 'recover', reason: 'account-access-mismatch' };
  }
  if (access.kind !== 'area') return recoveryFor(access);

  const expectedAccountType = access.area === 'sat' ? 'student' : 'guardian';
  if (user.family_account_type !== expectedAccountType) {
    return { kind: 'recover', reason: 'account-access-mismatch' };
  }

  const parentOnboardingPending = access.area === 'sat'
    && readParentOnboardingVersion(user) < CURRENT_FAMILY_PARENT_ONBOARDING_VERSION;
  if (parentOnboardingPending && !chooseRoleSuppressed) {
    return requestedArea === 'account-setup'
      ? { kind: 'allow', area: 'account-setup' }
      : { kind: 'redirect', destination: '/choose-role', reason: 'parent-onboarding' };
  }

  if (requestedArea === 'account-setup') {
    return {
      kind: 'redirect',
      destination: homeFor(access.area),
      reason: 'setup-complete',
    };
  }
  if (requestedArea !== access.area) {
    return {
      kind: 'redirect',
      destination: homeFor(access.area),
      reason: 'authorized-area',
    };
  }
  return { kind: 'allow', area: access.area };
}
